import { useNavigate } from 'react-router-dom';
import { Feature, useGetFeatureToggles } from '@urbanx/agx.agencies.client';
import { ProposalsDebugModal } from 'features/proposals/ProposalsDebugModal';
import React, { useEffect, useRef, useState } from 'react';
import { AgxButton, AgxPortal, Icon_CodeXml, useDialog } from '@urbanx/agx.ui-components2';
import DevCampaignScenarioModal from 'features/elements/BeginCampaign/DevCampaignScenario/DevCampaignScenarioModal';
import './IconBubble.scss';
import './DevMenuButton.scss';

interface MenuPosition {
  top: number;
  right: number;
}

const DevMenuButton = () => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [showDevMenu, setShowDevMenu] = useState(false);
  const devCampaignScenarioDialog = useDialog(DevCampaignScenarioModal);
  const navigate = useNavigate();
  const proposalsDialog = useDialog(ProposalsDebugModal, { onNavigate: navigate });
  const proposalsFeature = useGetFeatureToggles();
  const showProposals =
    !proposalsFeature.isPending &&
    !proposalsFeature.isError &&
    proposalsFeature.data?.enabledFeatures?.includes(Feature.Proposals);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    top: 0,
    right: 0,
  });

  useEffect(() => {
    if (!showDevMenu) return;

    const triggerRect = triggerRef.current?.getBoundingClientRect();
    if (!triggerRect) return;

    setMenuPosition({
      top: triggerRect.bottom + 8,
      right: window.innerWidth - triggerRect.right,
    });
  }, [showDevMenu]);

  useEffect(() => {
    if (!showDevMenu) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }

      setShowDevMenu(false);
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showDevMenu]);

  return (
    <>
      <div ref={triggerRef} className="devMenuShell">
        <button
          type="button"
          className="icon-bubble devIconBubble"
          aria-label="Open development menu"
          aria-expanded={showDevMenu}
          onClick={() => setShowDevMenu((isOpen) => !isOpen)}
        >
          <Icon_CodeXml color="var(--fg-brand-main)" />
        </button>
      </div>
      {showDevMenu && (
        <AgxPortal>
          <div
            ref={popoverRef}
            className="devMenuPopover"
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
            }}
          >
            <AgxButton
              id="dev-menu-create-test-campaign"
              text="Create Test Campaign"
              variant="white"
              size="small"
              stretch
              onClick={() => {
                setShowDevMenu(false);
                devCampaignScenarioDialog.open();
              }}
            />
            {showProposals && (
              <AgxButton
                id="dev-menu-proposals"
                text="Proposals debug"
                variant="white"
                size="small"
                stretch
                onClick={() => {
                  setShowDevMenu(false);
                  proposalsDialog.open();
                }}
              />
            )}
          </div>
        </AgxPortal>
      )}
    </>
  );
};

export default DevMenuButton;
